import { useRef } from 'react';

/**
 * Owns the one async attempt that is allowed to continue. Beginning a new attempt aborts the
 * previous one; the attempt checks its signal after each await and stops once aborted.
 *
 * It matters when an attempt spans several awaits and the user can act in between:
 *
 *   click Edit -> await save draft -> await create run -> poll
 *                       ^ user clicks Cancel while the save is in flight
 *
 * The save cannot be interrupted and resolves after the cancel. Without the signal check the
 * attempt would carry on and create a run the user already cancelled.
 */
export const useAbortableAttempt = () => {
  const abortRef = useRef<AbortController | null>(null);

  const attemptRef = useRef({
    begin: (): AbortSignal => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      return abortRef.current.signal;
    },
    abort: (): void => abortRef.current?.abort(),
  });
  return attemptRef.current;
};
